import { useState } from "react";
import { Link, Navigate, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, ShieldCheck } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { PasswordInput } from "../../components/dash/PasswordInput";
import { Label } from "../../components/ui/label";
import { Logo } from "../../components/Logo";
import { useAuth } from "../../context/AuthContext";
import { api, formatApiError } from "../../lib/api";
import { ROLE_HOME } from "../../lib/labels";
import { FullLoader } from "../../components/ProtectedRoute";
import { OtpForm } from "../../components/auth/OtpForm";

const AuthShell = ({ title, subtitle, children }) => (
  <div className="min-h-screen grid lg:grid-cols-2 bg-[#F8FAF6]">
    <div className="relative hidden lg:block">
      <img src="/images/coffee-hillside.jpg" alt="Kebun kopi di perbukitan" className="absolute inset-0 h-full w-full object-cover" />
      <div className="absolute inset-0 bg-emerald-950/70" />
      <div className="relative h-full flex flex-col justify-between p-12">
        <Logo variant="full" testid="auth-side-logo" className="w-44" />
        <div className="max-w-md">
          <ShieldCheck className="h-8 w-8 text-emerald-200" />
          <p className="mt-4 text-2xl font-bold text-white leading-snug">Akses aman untuk PT SJI, kelompok tani, dan investor.</p>
          <p className="mt-3 text-sm text-emerald-50/80">Setiap akun hanya dapat melihat data yang ditugaskan kepadanya.</p>
        </div>
      </div>
    </div>
    <div className="flex flex-col justify-center px-5 sm:px-12 py-12">
      <div className="w-full max-w-sm mx-auto">
        <div className="lg:hidden mb-8 flex justify-center"><Logo variant="full" testid="auth-mobile-logo" className="w-40" /></div>
        <h1 className="text-3xl font-extrabold tracking-tight text-slate-900">{title}</h1>
        <p className="mt-2 text-sm text-slate-600">{subtitle}</p>
        <div className="mt-8">{children}</div>
        <Link to="/" className="mt-10 inline-block text-sm text-slate-500 hover:text-emerald-800" data-testid="auth-back-home">← Kembali ke Beranda</Link>
      </div>
    </div>
  </div>
);

const ErrorBox = ({ error, testid }) =>
  error ? <p className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700" data-testid={testid}>{error}</p> : null;

export function LoginPage() {
  const { user, login, verifyOtp } = useAuth();
  const [challenge, setChallenge] = useState(null);
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const from = useLocation().state?.from;
  const target = (role) => (from && (from.startsWith(ROLE_HOME[role]) || from.startsWith("/buka/")) ? from
    : role === "INVESTOR" ? `${ROLE_HOME[role]}/rencana-kebun` : ROLE_HOME[role]);

  if (user === null) return <FullLoader />;
  if (user) return <Navigate to={target(user.role)} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await login(email, password);
      if (u.otpRequired) {
        setChallenge(u);
        return;
      }
      navigate(target(u.role), { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  if (challenge) {
    const onVerify = async (code, trust) => {
      const u = await verifyOtp(challenge.challengeId, code, trust);
      navigate(target(u.role), { replace: true });
    };
    return (
      <AuthShell title="Verifikasi Perangkat" subtitle="Langkah keamanan untuk login dari perangkat baru.">
        <OtpForm challenge={challenge} onVerify={onVerify} onBack={() => setChallenge(null)} />
      </AuthShell>
    );
  }

  return (
    <AuthShell title="Masuk" subtitle="Gunakan akun yang diberikan oleh PT SJI.">
      <form onSubmit={submit} className="space-y-5" data-testid="login-form">
        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <Input id="email" type="email" required autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} className="h-11 bg-white" data-testid="login-email-input" />
        </div>
        <div className="space-y-2">
          <div className="flex justify-between items-center">
            <Label htmlFor="password">Kata Sandi</Label>
            <Link to="/lupa-password" className="text-xs font-semibold text-emerald-800" data-testid="forgot-password-link">Lupa kata sandi?</Link>
          </div>
          <PasswordInput id="password" required autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} className="h-11 bg-white" data-testid="login-password-input" />
        </div>
        <ErrorBox error={error} testid="login-error" />
        <Button type="submit" disabled={loading} className="w-full h-11 bg-emerald-800 hover:bg-emerald-900" data-testid="login-submit-btn">
          {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Masuk
        </Button>
        <p className="text-xs text-slate-500">Belum memiliki akun? Akun kelompok tani dibuat oleh PT SJI. Calon Pemodal: <Link to="/ingin-menjadi-pemodal" className="font-semibold text-emerald-800 underline" data-testid="login-investor-apply-link">ajukan diri di sini</Link>.</p>
        <Link to="/daftar-pemilik-lahan" className="text-xs font-semibold text-emerald-800" data-testid="register-owner-link">Pemilik kebun kopi pribadi? Daftar di sini</Link>
      </form>
    </AuthShell>
  );
}

export function RegisterOwnerPage() {
  const [f, setF] = useState({ name: "", email: "", phone: "", password: "", landLocation: "" });
  const [msg, setMsg] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try { const { data } = await api.post("/auth/register-owner", f); setMsg(data.message); }
    catch (err) { setError(err?.response?.status === 422 ? "Periksa data: nomor HP angka saja, email valid, kata sandi minimal 8 karakter." : formatApiError(err)); }
    finally { setLoading(false); }
  };
  return (
    <AuthShell title="Daftar Pemilik Lahan" subtitle="Untuk pemilik kebun kopi pribadi yang ingin kebunnya dikelola Kelompok Tani / Koperasi melalui Salimbado Farm. Akun aktif setelah disetujui PT SJI.">
      {msg ? <p className="rounded-lg bg-emerald-50 border border-emerald-200 px-4 py-3 text-sm text-emerald-800" data-testid="register-success">{msg}</p> : (
        <form onSubmit={submit} className="space-y-4" data-testid="register-owner-form">
          {[["name", "Nama lengkap", "text"], ["email", "Email", "email"], ["phone", "Nomor HP / WhatsApp", "tel"], ["password", "Kata sandi (min. 8 karakter)", "password"], ["landLocation", "Lokasi kebun (opsional)", "text"]].map(([k, l, t]) => (
            <div key={k} className="space-y-1.5"><Label htmlFor={`ro-${k}`}>{l}</Label>{t === "password" ? <PasswordInput id="ro-password" required minLength={8} autoComplete="new-password" value={f[k]} onChange={set(k)} className="h-11 bg-white" data-testid="register-password-input" /> : <Input id={`ro-${k}`} type={t} required={k !== "landLocation"} minLength={k === "password" ? 8 : undefined} value={f[k]} onChange={set(k)} className="h-11 bg-white" data-testid={`register-${k}-input`} />}</div>
          ))}
          <ErrorBox error={error} testid="register-error" />
          <Button type="submit" disabled={loading} className="w-full h-11 bg-emerald-800 hover:bg-emerald-900" data-testid="register-submit-btn">{loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Daftar</Button>
        </form>
      )}
      <Link to="/login" className="mt-6 inline-block text-sm font-semibold text-emerald-800" data-testid="register-back-login">Kembali ke halaman masuk</Link>
    </AuthShell>
  );
}

export function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [msg, setMsg] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      setMsg(data.message);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell title="Lupa Kata Sandi" subtitle="Masukkan email akun Anda. Kami akan mengirim tautan untuk mengatur ulang kata sandi.">
      {msg ? (
        <p className="rounded-lg bg-emerald-50 border border-emerald-200 px-4 py-3 text-sm text-emerald-800" data-testid="forgot-success">{msg}</p>
      ) : (
        <form onSubmit={submit} className="space-y-5" data-testid="forgot-form">
          <div className="space-y-2">
            <Label htmlFor="femail">Email</Label>
            <Input id="femail" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="h-11 bg-white" data-testid="forgot-email-input" />
          </div>
          <ErrorBox error={error} testid="forgot-error" />
          <Button type="submit" disabled={loading} className="w-full h-11 bg-emerald-800 hover:bg-emerald-900" data-testid="forgot-submit-btn">
            {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Kirim Tautan
          </Button>
        </form>
      )}
      <Link to="/login" className="mt-6 inline-block text-sm font-semibold text-emerald-800" data-testid="back-to-login">Kembali ke halaman masuk</Link>
    </AuthShell>
  );
}

export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [msg, setMsg] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) return setError("Konfirmasi kata sandi tidak sama");
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/reset-password", { token, password });
      setMsg(data.message);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell title="Atur Ulang Kata Sandi" subtitle="Buat kata sandi baru minimal 8 karakter.">
      {msg ? (
        <p className="rounded-lg bg-emerald-50 border border-emerald-200 px-4 py-3 text-sm text-emerald-800" data-testid="reset-success">{msg}</p>
      ) : (
        <form onSubmit={submit} className="space-y-5" data-testid="reset-form">
          <div className="space-y-2">
            <Label htmlFor="np">Kata sandi baru</Label>
            <PasswordInput id="np" minLength={8} required value={password} onChange={(e) => setPassword(e.target.value)} className="h-11 bg-white" data-testid="reset-password-input" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="cp">Konfirmasi kata sandi</Label>
            <PasswordInput id="cp" minLength={8} required value={confirm} onChange={(e) => setConfirm(e.target.value)} className="h-11 bg-white" data-testid="reset-confirm-input" />
          </div>
          <ErrorBox error={!token ? "Tautan tidak valid" : error} testid="reset-error" />
          <Button type="submit" disabled={loading || !token} className="w-full h-11 bg-emerald-800 hover:bg-emerald-900" data-testid="reset-submit-btn">
            {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Simpan Kata Sandi
          </Button>
        </form>
      )}
      <Link to="/login" className="mt-6 inline-block text-sm font-semibold text-emerald-800" data-testid="reset-back-to-login">Ke halaman masuk</Link>
    </AuthShell>
  );
}
